import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmail from 'validator/es/lib/isEmail';
import ReviewUser from 'components/ReviewUser/ReviewUser';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { approveEmployeeRequest, Employee, selectEmployee } from 'store/Employee';
import { IStore } from 'store/types';
import { EmployeeReviewFieldKey, IEmployeeReviewField } from 'types';
import { formatDateForUI } from 'utils/date';
import { formatMobile } from 'utils/strings';

interface IReviewUserScreenProps {
  onClose?: () => void;
  onDeny?: () => void;
  onApprove?: () => void;
  visible: boolean;
}

const ReviewUserScreen = ({ onClose, onDeny, onApprove, visible }: IReviewUserScreenProps) => {
  const dispatch = useDispatch();
  const isReviewEmployeeLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.GET_EMPLOYEE_REQUEST));

  const reviewEmployeeDetail = useSelector(selectEmployee());
  const reviewEmployeeStatusFailures = reviewEmployeeDetail?.approvalFailures;
  const firstNameFailure = (reviewEmployeeStatusFailures || []).find((failure) => failure.field === 'first_name');
  const lastNameFailure = (reviewEmployeeStatusFailures || []).find((failure) => failure.field === 'last_name');
  const identityFailure = (reviewEmployeeStatusFailures || []).find((failure) => failure.field === 'invite_recipient');

  const formatIdentity = (identity: string): string => {
    if (isEmail(identity)) return identity;
    return formatMobile(identity) || '';
  };

  const reviewEmployeeInviteRecipient = reviewEmployeeDetail?.inviteRecipient || '';
  const reviewEmployeeFirstName = reviewEmployeeDetail?.firstName || '';
  const reviewEmployeeLastName = reviewEmployeeDetail?.lastName || '';
  const matchedIdentityVal = formatIdentity(reviewEmployeeInviteRecipient);

  const reviewObject: Record<EmployeeReviewFieldKey, IEmployeeReviewField> = {
    [EmployeeReviewFieldKey.FIRST_NAME]: firstNameFailure
      ? {
          employee: firstNameFailure.employeeValue as string,
          user: firstNameFailure.userValue as string,
        }
      : {
          employee: reviewEmployeeFirstName,
          user: reviewEmployeeFirstName,
        },
    [EmployeeReviewFieldKey.LAST_NAME]: lastNameFailure
      ? {
          employee: lastNameFailure.employeeValue as string,
          user: lastNameFailure.userValue as string,
        }
      : {
          employee: reviewEmployeeLastName,
          user: reviewEmployeeLastName,
        },
    [EmployeeReviewFieldKey.IDENTITY_RECIPIENT]: identityFailure
      ? {
          employee: formatIdentity(identityFailure.employeeValue || ''),
          user: formatIdentity(identityFailure.userValue || ''),
        }
      : {
          employee: matchedIdentityVal,
          user: matchedIdentityVal,
        },
    [EmployeeReviewFieldKey.DATE_OF_BIRTH]: {
      employee: '',
      user: reviewEmployeeDetail?.user?.dateOfBirth ? formatDateForUI(reviewEmployeeDetail.user.dateOfBirth) : '',
    },
  };

  const onApproveSuccess = () => {
    onApprove?.();
    onClose?.();
  };

  return (
    <>
      <ReviewUser
        avatarUrl={reviewEmployeeDetail?.user?.avatarUrl}
        reviewObject={reviewObject}
        isActive={visible}
        isLoading={isReviewEmployeeLoading}
        onClose={() => {
          onClose?.();
        }}
        onIgnore={() => {
          onDeny?.();
        }}
        onApprove={() => {
          if (reviewEmployeeDetail?.id) {
            dispatch(approveEmployeeRequest(reviewEmployeeDetail.id, onApproveSuccess));
          }
        }}
        userName={reviewEmployeeDetail?.name || ''}
      />
    </>
  );
};

export default ReviewUserScreen;
